import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import ThemeModeToggle from "../components/layout/ThemeModeToggle";
import samiLogo from "../assets/sami_lite.svg";
import samiLogoInverted from "../assets/sami-logo-inverted.svg";
import type { RegisterOrganizationPayload } from "../types";

const OrgSignupPage: React.FC = () => {
  const theme = useTheme();
  const { identityEmail, registerOrganization, error } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const accountEmail = useMemo(
    () => identityEmail?.trim() || "Unknown",
    [identityEmail],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    const trimmedCompanyName = companyName.trim();
    if (!trimmedCompanyName) {
      setLocalError("Common name is required.");
      return;
    }

    const payload: RegisterOrganizationPayload = {
      preferredOrgName: trimmedCompanyName,
      companyName: trimmedCompanyName,
    };
    const trimmedAddress = address.trim();
    const trimmedWebsite = website.trim();
    const trimmedPhone = phone.trim();
    const trimmedContactEmail = contactEmail.trim();
    if (trimmedAddress) payload.address = trimmedAddress;
    if (trimmedWebsite) payload.website = trimmedWebsite;
    if (trimmedPhone) payload.phone = trimmedPhone;
    if (trimmedContactEmail) payload.contactEmail = trimmedContactEmail;

    setSubmitting(true);
    try {
      await registerOrganization(payload);
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : "Failed to create organization",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = localError ?? error;
  const logoSrc =
    theme.palette.mode === "dark" ? samiLogoInverted : samiLogo;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "background.default",
        py: 4,
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeModeToggle />
      </Box>
      <Container maxWidth="sm">
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box
              component="img"
              src={logoSrc}
              alt="SAMi — Innovative-Integrated"
              sx={{
                display: "block",
                width: "100%",
                maxWidth: 200,
                mx: "auto",
                mb: 3,
              }}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Create your organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Welcome. Lets get started. Once we have a few detail, we will
              create a space (org) for you
            </Typography>

            {displayError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {displayError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Your account email"
                  value={accountEmail}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="a.k.a Tenant Admin / Owner Email"
                />
                <TextField
                  label="Common Name"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                  fullWidth
                  required
                  helperText="ex Sams Digitial Space"
                />
                <TextField
                  label="Address"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Phone"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Contact email"
                  type="email"
                  value={contactEmail}
                  onChange={(event) => setContactEmail(event.target.value)}
                  fullWidth
                  helperText="Optional alternate contact email."
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 3, textTransform: "none", fontWeight: 600 }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    Creating organization...
                  </>
                ) : (
                  "Create organization"
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default OrgSignupPage;
